import { Action, Reducer } from 'redux';

import {
    IdName,
    ManagedGroupPricing,
    RestaurantGroup,
    RestaurantPaymentDetailsModel,
    TaxResponseModel,
} from 'api/models';
import { RoleData } from '../rolesStore/reducer';
import { RestaurantUserData } from '../usersStore/reducer';
import { RestaurantDetailsAction } from './actions';
import { OrderServices, Positions, TermsContentType } from 'constants/enums';
import { ServiceValue } from 'components/restaurantInformation/components/types';

export interface RestaurantDetailsState {
    restaurant: RestaurantDetails;
    restaurantInformation: RestaurantDetails;
    isRestaurantInformationSettled: boolean;
    cuisineTypes: CuisineData[];
    taxes: TaxResponseModel;
    qrCodes: QRData[];
    qrCodesLoading: boolean;
    responsibleUsers: ResponsibleUser[];
    stornoReasons: IdName[];
    groups: RestaurantGroup[];
    managedGroups: RestaurantGroup[];
}

export interface CuisineData {
    id: string;
    name: string;
    inUse: number;
}

export interface RestaurantContactPerson {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    isEnabled: boolean;
    restaurants: RestaurantUserData[];
    roles: RoleData[];
}

export interface OrderOverviewPrintoutSettings {
    isEnabled: boolean;
    printingStations: string[];
}

export interface RestaurantAdvancedSettings {
    isPrintingEnabled: boolean;
    orderOverviewPrintout: OrderOverviewPrintoutSettings;
    receiptLogoUrl?: string;
}

export interface ReservationModule {
    isEnabled: boolean;
    isOnlineAvailable: boolean;
    isSameDayAvailable: boolean;
    bookingHorizon: number | null;
    maxGuestsOnline: number | null;
    maxReservationDurationMinutes: number;
    sameDayUntilTime: number;
}

export interface ReservationModuleUpdateModel {
    isOnlineAvailable: boolean;
    isSameDayAvailable: boolean;
    bookingHorizon: number;
    maxGuestsOnline: number;
    maxReservationDurationMinutes: number;
    sameDayUntilTime: number;
    conditionsText: string;
}

export interface ReservationModuleSettings extends ReservationModule {
    conditionsText: string;
}

export interface AutomaticZClosingSettings {
    isEnabled: boolean;
    generationHour: number;
    generationMinute: number;
    nextGenerationDate?: number;
    nextGenerationBusinessDay?: number;
}

export interface RestaurantDetails {
    id: string;
    name: string;
    addressLine: string;
    zip: string;
    city: string;
    phoneNumber: string;
    email: string;
    website: string;
    latitude: number;
    longitude: number;
    contactPersons: RestaurantContactPerson[];
    logoUrl?: string;
    photoUrl?: string;
    percentageForDineIn?: number;
    percentageForTakeAway?: number;
    percentageForHomeDelivery?: number;
    paymentDetails: PaymentDetails;
    timeZone: string;
    openHours?: any[];
    qrCodesData: QRCodesData;
    homeDelivery: ServiceValue;
    takeAway: ServiceValue;
    dineIn: ServiceValue;
    takeOut: ServiceValue;
    pricingLevels: IdName[];
    groupPricingLevels?: IdName[];
    managedGroupPricingLevels?: ManagedGroupPricing[];
    coolBoxData: CoolBoxData;
    type: any[];
    isDeliverAnytime: boolean;
    reservationModule?: ReservationModule;
    advancedSettings?: RestaurantAdvancedSettings;
    automaticZClosingSettings?: AutomaticZClosingSettings;
    termsAndConditions: TermsAndConditions;
    description: string;
    facebook?: string;
    instagram?: string;
    addToGallery?: boolean;
    ordersDeadline?: number;
    isPayToClose?: boolean;
    isOrderInAdvance?: boolean;
}

export interface CoolBoxData {
    coolBoxName: string;
    isCoolBox: boolean;
    isPublic: boolean;
}

export interface QRCodesData {
    dineInQrCodeName: string;
    takeAwayQrCodeName: string;
    homeDeliveryQrName: string;
}

export interface QRData {
    id?: string;
    name: string;
    service: OrderServices;
    url: string;
    area: string;
    areaId?: string;
}

export interface ResponsibleUser {
    id: string;
    position: Positions;
    name: string;
}

export interface PaymentDetails extends RestaurantPaymentDetailsModel {}

export interface TermsAndConditions {
    contentType: TermsContentType;
    linkContent: string;
    htmlContent: string;
}

//ACTIONS

//ACTION CREATORS

//REDUCER
const initialState: RestaurantDetailsState = {
    restaurant: {
        id: '',
        name: '',
        addressLine: '',
        zip: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        latitude: 0,
        longitude: 0,
        contactPersons: [],
        paymentDetails: {
            firmName: '',
            firstName: '',
            lastName: '',
            address: '',
            zip: '',
            city: '',
            accountNumber: '',
            iban: '',
            vat: '',
            tax: 0,
            currency: '',
            thanksMessage: '',
        },
        timeZone: 'Europe/Zurich',
        qrCodesData: { dineInQrCodeName: '', takeAwayQrCodeName: '', homeDeliveryQrName: '' },
        dineIn: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        takeAway: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        homeDelivery: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        takeOut: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        pricingLevels: [],
        coolBoxData: {
            isCoolBox: false,
            coolBoxName: '',
            isPublic: false,
        },
        type: [],
        isDeliverAnytime: false,
        termsAndConditions: {
            contentType: TermsContentType.None,
            linkContent: '',
            htmlContent: '',
        },
        description: '',
    },
    restaurantInformation: {
        name: '',
        id: '',
        addressLine: '',
        zip: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        latitude: 0,
        longitude: 0,
        contactPersons: [],
        paymentDetails: {
            firmName: '',
            firstName: '',
            lastName: '',
            address: '',
            zip: '',
            city: '',
            accountNumber: '',
            iban: '',
            vat: '',
            tax: 0,
            currency: '',
            thanksMessage: '',
        },
        timeZone: 'Europe/Zurich',
        qrCodesData: { dineInQrCodeName: '', takeAwayQrCodeName: '', homeDeliveryQrName: '' },
        dineIn: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        takeAway: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        homeDelivery: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        takeOut: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
            allowUnpaidOrder: false,
        },
        pricingLevels: [],
        coolBoxData: {
            isCoolBox: false,
            coolBoxName: '',
            isPublic: false,
        },
        type: [],
        isDeliverAnytime: false,
        termsAndConditions: {
            contentType: TermsContentType.None,
            linkContent: '',
            htmlContent: '',
        },
        description: '',
    },
    cuisineTypes: [],
    taxes: {
        id: '',
        standardRate: 0,
        reducedRate: 0,
        specialRateForAccommodation: 0,
        noVAT: 0,
    },
    stornoReasons: [],
    qrCodes: [],
    qrCodesLoading: true,
    isRestaurantInformationSettled: false,
    responsibleUsers: [],
    groups: [],
    managedGroups: [],
};

function mapGroupLevels(
    groups: ManagedGroupPricing[] | undefined,
    groupId: string,
    updateLevels: (levels: IdName[]) => IdName[]
): ManagedGroupPricing[] {
    return (groups ?? []).map((group) =>
        group.groupId === groupId
            ? { ...group, pricingLevels: updateLevels(group.pricingLevels) }
            : group
    );
}

export const reducer: Reducer<RestaurantDetailsState> = (
    state: RestaurantDetailsState | undefined,
    incomingAction: Action
): RestaurantDetailsState => {
    if (state === undefined) {
        return initialState;
    }
    const action = incomingAction as RestaurantDetailsAction;

    switch (action.type) {
        case 'RESTAURANT_DETAILS_GET_SUCCESS':
            return {
                ...state,
                restaurant: action.restaurant,
            };
        case 'RESTAURANT_DETAILS_UPDATE_SUCCESS':
            return {
                ...state,
                restaurant: action.restaurant,
            };
        case 'GET_RESTAURANT_INFORMATION_SUCCESS': {
            return {
                ...state,
                restaurantInformation: action.restaurantInformation,
                isRestaurantInformationSettled: true,
            };
        }
        case 'GET_RESTAURANT_INFORMATION_ERROR':
            return { ...state, isRestaurantInformationSettled: true };
        case 'USER_GET_RESTAURANTS_SUCCESS':
            return action.restaurants.currentRestaurantId
                ? state
                : { ...state, isRestaurantInformationSettled: true };
        case 'USER_GET_RESTAURANTS_ERROR':
            return { ...state, isRestaurantInformationSettled: true };
        case 'GET_CUISINE_TYPES_SUCCESS': {
            return {
                ...state,
                cuisineTypes: action.data,
            };
        }
        case 'UPDATE_RESTAURANT_INFORMATION_SUCCESS': {
            return {
                ...state,
                restaurantInformation: action.restaurantInformation,
            };
        }
        case 'UPDATE_RESERVATION_MODULE_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    reservationModule: action.reservationModule,
                },
            };
        }
        case 'GET_QR_CODES_SUCCESS': {
            return {
                ...state,
                qrCodes: action.data,
                qrCodesLoading: false,
            };
        }
        case 'DELETE_QR_CODE_SUCCESS': {
            //@ts-ignore
            const newQrCodes = state.qrCodes.filter((qrCode) => !action.data.includes(qrCode.id));
            return {
                ...state,
                qrCodes: newQrCodes,
            };
        }
        case 'UPDATE_QR_CODE_SUCCESS': {
            const restOfQrs = state.qrCodes.filter((qrCode) => qrCode.id !== action.data.id);
            return {
                ...state,
                qrCodes: [action.data, ...restOfQrs],
            };
        }
        case 'UPDATE_CONTACT_PERSONS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    contactPersons: state.restaurantInformation.contactPersons.map((x) =>
                        action.users.find((y) => y.id === x.id)
                            ? action.users.find((y) => y.id === x.id)!
                            : x
                    ),
                },
            };
        }
        case 'UPDATE_PAYMENT_DETAILS_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    paymentDetails: action.data,
                },
            };
        }
        case 'UPDATE_QR_CODES_NAME_SUCCESS': {
            return {
                ...state,
                restaurantInformation: { ...state.restaurantInformation, qrCodesData: action.data },
            };
        }
        case 'GET_RESTAURANT_RESPONSIBLE_USERS_SUCCESS': {
            return {
                ...state,
                responsibleUsers: action.data,
            };
        }
        case 'GET_TAXES_SUCCESS': {
            return {
                ...state,
                taxes: action.data,
            };
        }
        case 'ADD_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    pricingLevels: [...state.restaurantInformation.pricingLevels, action.payload],
                },
            };
        }
        case 'UPDATE_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    pricingLevels: state.restaurantInformation.pricingLevels.map((x) =>
                        x.id === action.payload.id ? action.payload : x
                    ),
                },
            };
        }
        case 'DELETE_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    pricingLevels: state.restaurantInformation.pricingLevels.filter(
                        (x) => x.id !== action.payload
                    ),
                },
            };
        }
        case 'ADD_GROUP_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    groupPricingLevels: [
                        ...(state.restaurantInformation.groupPricingLevels ?? []),
                        action.payload,
                    ],
                    managedGroupPricingLevels: mapGroupLevels(
                        state.restaurantInformation.managedGroupPricingLevels,
                        action.groupId,
                        (levels) => [...levels, action.payload]
                    ),
                },
            };
        }
        case 'UPDATE_GROUP_PRICING_LEVEL_SUCCESS': {
            const renameLevel = (x: IdName) => (x.id === action.payload.id ? action.payload : x);
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    groupPricingLevels: (state.restaurantInformation.groupPricingLevels ?? []).map(
                        renameLevel
                    ),
                    managedGroupPricingLevels: mapGroupLevels(
                        state.restaurantInformation.managedGroupPricingLevels,
                        action.groupId,
                        (levels) => levels.map(renameLevel)
                    ),
                },
            };
        }
        case 'DELETE_GROUP_PRICING_LEVEL_SUCCESS': {
            const isNotDeleted = (x: IdName) => x.id !== action.payload;
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    groupPricingLevels: (
                        state.restaurantInformation.groupPricingLevels ?? []
                    ).filter(isNotDeleted),
                    managedGroupPricingLevels: mapGroupLevels(
                        state.restaurantInformation.managedGroupPricingLevels,
                        action.groupId,
                        (levels) => levels.filter(isNotDeleted)
                    ),
                },
            };
        }
        case 'UPDATE_RESTAURANT_ADVANCED_SETTINGS_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    advancedSettings: action.data,
                },
            };
        }
        case 'UPDATE_AUTOMATIC_Z_CLOSING_SETTINGS_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    automaticZClosingSettings: action.data,
                },
            };
        }
        case 'GET_STORNO_REASONS_SUCCESS': {
            return {
                ...state,
                stornoReasons: action.data,
            };
        }
        case 'GET_MY_GROUPS': {
            const sorted = action.payload.sort((a, b) => a.name.localeCompare(b.name));
            sorted.forEach((g) => g.restaurants.sort((a, b) => a.name.localeCompare(b.name)));
            return {
                ...state,
                groups: sorted,
            };
        }
        case 'GET_CURRENT_RESTAURANT_MANAGED_GROUPS': {
            const sorted = action.payload.sort((a, b) => a.name.localeCompare(b.name));
            sorted.forEach((g) => g.restaurants.sort((a, b) => a.name.localeCompare(b.name)));
            return {
                ...state,
                managedGroups: sorted,
            };
        }
        case 'RESTAURANT_UPDATE_SERVICE_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    dineIn: action.payload.dineIn,
                    takeAway: action.payload.takeAway,
                    homeDelivery: action.payload.homeDelivery,
                    takeOut: action.payload.takeOut,
                },
            };
        }
        default:
            return state;
    }
};
